/*
 * This file is part of the Akershus Eiendom Nettsted 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import debounce from 'lodash/debounce';
import api from 'utils/api';
import nextTick from 'utils/nextTick';
// import useEnhanced from 'utils/enhanced';
import Input from 'components/Input/Input';
import Magnifier from 'components/Icons/Magnifier/Magnifier';
import Arrow from 'components/Icons/Arrow/Arrow';
import Business from './Business/Business';
import './Contact.scss';

const callApi = debounce((action, onSuccess, onError) => {
  api(action)
    .then(onSuccess)
    .catch(onError);
}, 300);

function groupByBusiness(persons) {
  const result = {};

  persons.forEach(({ businesses, ...rest }) => {
    if (businesses && businesses.length) {
      if (!result[businesses[0].id]) {
        result[businesses[0].id] = {
          name: businesses[0].name,
          persons: [],
        };
      }
      result[businesses[0].id].persons.push({
        ...rest,
        businesses,
      });
    }
  });

  Object.keys(result).forEach(key => {
    result[key].persons = result[key].persons.sort((a, b) => {
      if (a.name < b.name) return -1;
      if (a.name > b.name) return 1;
      return 0;
    });
  });

  return result;
}

/**
 * This is the Contact component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */
function Contact({
  open,
  action,
  info,
  searching,
  placeholder,
  hint,
  all,
  employees,
  onFocus,
  labels,
}) {
  const content = useRef(null);
  const infoPane = useRef(null);
  const searchPane = useRef(null);
  const input = useRef(null);
  const [frase, setFrase] = useState('');
  const [loading, setLoading] = useState(false);
  const [matches, setMatches] = useState([]);
  const [error, setError] = useState(null);
  // const enhanced = useEnhanced(1024);

  useEffect(() => {
    if (open) {
      infoPane.current.querySelector('a').focus();
    }
  }, [open]);

  useEffect(() => {
    setLoading(false);
    setError(null);

    if (frase.length) {
      const actionFrase = action.replace('{frase}', frase);
      setLoading(true);
      callApi(
        actionFrase,
        matches => {
          setLoading(false);
          setMatches(matches);
        },
        ({ error }) => {
          setLoading(false);
          setError(error);
        }
      );
    } else {
      setMatches([]);
    }

    return function cleanup() {
      callApi.cancel();
    };
  }, [frase, action]);

  const onHintScroll = debounce(() => {
    content.current.parentNode.removeEventListener('scroll', onHintScroll);
    input.current.focus();
  }, 100);

  function onHintClick(e) {
    if (typeof searchPane.current.scrollIntoView === 'function') {
      e.preventDefault();
      content.current.parentNode.addEventListener('scroll', onHintScroll);
      searchPane.current.scrollIntoView({ behavior: 'smooth' });
    } else {
      nextTick(() => {
        input.current.focus();
      });
    }
  }

  function renderError() {
    if (!error) return null;

    return <strong>{error}</strong>;
  }

  function renderBusinesses() {
    if (!open) return null;

    const businesses = matches.length
      ? groupByBusiness(matches)
      : groupByBusiness(employees.preview);

    return (
      <div className="businesses">
        {Object.keys(businesses).map(key => (
          <Business
            key={key}
            label={matches.length ? null : employees.label}
            cv={employees.cv}
            {...businesses[key]}
          />
        ))}
      </div>
    );
  }

  function renderAll(className = null) {
    if (!open) return null;
    if (!all) return null;

    return (
      <a className={classnames('button yellow', className)} href={all.url}>
        {all.text}
      </a>
    );
  }

  function renderFax() {
    if (!info.fax.value) return null;

    return (
      <div>
        <strong>{info.fax.label}</strong>
        <a tabIndex={open ? null : -1} href={`tel:${info.fax.value}`}>
          {info.fax.value}
        </a>
      </div>
    );
  }

  return (
    <div
      ref={content}
      id="contact"
      role="tabpanel"
      aria-labelledby="contactbutton"
      aria-hidden={!open}
      className={classnames('content-contact', { open })}
      onFocus={() => {
        onFocus('contact');
      }}
    >
      <div
        role="group"
        aria-label={labels.title}
        ref={infoPane}
        className="page-padding info"
      >
        <div className="methods">
          <div role="group" aria-label={labels.address} className="address">
            <strong>{info.address.label}</strong>
            <div dangerouslySetInnerHTML={{ __html: info.address.value }} />
          </div>
          <div role="group" aria-label={labels.phone} className="tele">
            <div>
              <strong>{info.phone.label}</strong>
              <a tabIndex={open ? null : -1} href={`tel:${info.phone.value}`}>
                {info.phone.value}
              </a>
            </div>
            {renderFax()}
          </div>
          <div className="services">
            <div role="group" aria-label={labels.email}>
              <strong>{info.email.label}</strong>
              <a
                tabIndex={open ? null : -1}
                href={`mailto:${info.email.value}`}
              >
                {info.email.value}
              </a>
            </div>
            <div className="socials" role="group" aria-label={labels.socials}>
              <strong>{info.socials.title}</strong>
              <div>
                {info.socials.items.map(({ name, href }) => (
                  <span key={name}>
                    <a tabIndex={open ? null : -1} href={href}>
                      {name}
                    </a>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
        <a
          tabIndex={open ? null : -1}
          href="#employees-search"
          className="hint"
          onClick={onHintClick}
        >
          <Arrow className="animate-bounce" down />
          {hint}
        </a>
      </div>
      <div
        id="employees-search"
        role="group"
        aria-label={labels.employees}
        ref={searchPane}
        className="page-padding search-pane"
      >
        <div className="header">
          <h2>
            <strong>{employees.count}</strong> {employees.title}
          </h2>
          {renderAll('desktop')}
        </div>
        <Input
          ref={input}
          tabIndex={open ? null : -1}
          className="search-input"
          loading={loading}
          loadingText={searching}
          value={frase}
          placeholder={placeholder}
          icon={<Magnifier aria-hidden="true" />}
          onChange={({ target: { value } }) => {
            setFrase(value);
          }}
        />
        {renderError()}
        {renderAll('mobile')}
        {renderBusinesses()}
        {renderAll()}
      </div>
    </div>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
Contact.propTypes = {
  action: PropTypes.string.isRequired,
  open: PropTypes.bool,
  searching: PropTypes.string,
  placeholder: PropTypes.string,
  hint: PropTypes.string,
  all: PropTypes.shape({
    text: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
  }),
  info: PropTypes.shape({
    title: PropTypes.string.isRequired,
    address: PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }).isRequired,
    phone: PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }).isRequired,
    fax: PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string,
    }).isRequired,
    email: PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }).isRequired,
    socials: PropTypes.shape({
      title: PropTypes.string.isRequired,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          href: PropTypes.string.isRequired,
        })
      ).isRequired,
    }).isRequired,
  }).isRequired,
  employees: PropTypes.shape({
    title: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    count: PropTypes.number.isRequired,
    cv: PropTypes.string.isRequired,
    preview: PropTypes.array.isRequired,
  }).isRequired,
  onFocus: PropTypes.func.isRequired,
  labels: PropTypes.shape({
    title: PropTypes.string.isRequired,
    address: PropTypes.string.isRequired,
    phone: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    socials: PropTypes.string.isRequired,
    employees: PropTypes.string.isRequired,
  }).isRequired,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
Contact.defaultProps = {
  open: false,
  searching: 'Searching',
  placeholder: 'Name / title / business',
  hint: 'Search staff',
  count: 0,
  onFocus: () => {},
  labels: {
    title: 'Contact',
    address: 'Address',
    phone: 'Phone',
    email: 'Email',
    socials: 'Social medias',
    employees: 'Employees',
  },
};

export default Contact;
